'use client';

import { ScrollArea } from '@/components/ui/scroll-area';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChevronLeft,
  Home,
  MapPin,
  User,
  ShoppingBag,
  Settings,
  Facebook,
  Instagram,
  Twitter,
  Loader2,
  CheckSquare,
  Square,
  Youtube,
  Linkedin,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import BottomNav from '@/components/layouts/Footer/BottomNav';
import { Input } from '@/components/ui/input';
import { useUser } from '../../contexts/UserContext';
import axiosInstance from '../../utils/axiosInstance';

const VendorProfile = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [deliveryCharges, setDeliveryCharges] = useState('');
  const [minimumOrderValue, setMinimumOrderValue] = useState('');
  const [addDeliveryCharges, setAddDeliveryCharges] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const { user, refreshUser } = useUser();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [showAlternateContactModal, setShowAlternateContactModal] = useState(false);
  const [alternateContact, setAlternateContact] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      setAddDeliveryCharges(user?.deliveryChargeWaived);
      setMinimumOrderValue(user?.minimumOrder);
      setDeliveryCharges(user?.deliveryCharge);
      setName(user?.name);
      setEmail(user?.emailId);

      // Extract category IDs from vendorCategories objects
      if (user?.vendorCategories && Array.isArray(user.vendorCategories)) {
        setSelectedCategories(user.vendorCategories);
      } else {
        setSelectedCategories([]);
      }
    }
  }, [user]);

  useEffect(() => {
    if (user?.alternateContact) {
      setAlternateContact(user.alternateContact);
    }
  }, [user?.alternateContact]);

  const fetchData = async () => {
    const phone = localStorage.getItem('contactNo');
    console.log(phone);

    if (!phone) {
      console.log('error');
      return;
    }
    refreshUser(phone);
  };

  const handleLogout = () => {
    logout();
  };

  const handleUpdate = async (categoryIds, offHours) => {
    setLoading(true);

    const apiData = {
      name,
      shopName: user?.shopName,
      emailId: email,
      contactNo: user?.contactNo,
      alternateContact: user?.alternateContact,
      minimumOrder: minimumOrderValue,
      deliveryCharge: deliveryCharges,
      deliveryChargeWaived: addDeliveryCharges,
      categoryIds: categoryIds,
      offHours: offHours || '',
    };

    console.log('Sending category IDs to API:', apiData);

    try {
      await axiosInstance.put(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/vendor`,
        apiData,
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
        }
      );
      fetchData();
      window / location.reload();
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleOutOfStation = async () => {
    try {
      if (!user?.id) {
        window.location.reload();
        return;
      }

      let status;
      if (user?.status === 'Approved') {
        status = 'OutOfStation';
      } else if (user?.status === 'OutOfStation') {
        status = 'Approved';
      } else {
        return;
      }

      setLoading(true);
      const { data } = await axiosInstance.put(
        `${import.meta.env.VITE_API_URL}/rest/admin/dashboard/api/v1/vendor/status?vendorId=${user?.id}&status=${status}`,
        {},
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
        }
      );

      setName(data?.name);
      setEmail(data?.emailId);
      setDeliveryCharges(data?.deliveryCharge);
      setMinimumOrderValue(data?.minimumOrder);
      setAddDeliveryCharges(data?.deliveryChargeWaived);
      setSelectedCategories(data?.categoryIds || []);

      fetchData();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col max-w-md mx-auto sm:border-l sm:border-r min-h-screen bg-gray-100">
      <header className="sticky top-0 z-50 bg-green-800 text-white">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <ChevronLeft onClick={() => navigate(-1)} className="h-6 w-6 cursor-pointer" />
          <h1 className="ml-2 text-xl font-semibold">My Account</h1>
          <Home onClick={() => navigate('/')} className="h-6 w-6 cursor-pointer ml-auto" />
        </div>
      </header>

      <main className="flex-grow container mx-auto pb-16 pt-4 px-4 space-y-3">
        <Card className="bg-white shadow-sm relative">
          <CardHeader className="px-4 py-3">
            <CardTitle className="text-lg font-semibold flex items-center">
              <div className="flex gap-1 items-center">
                <User className="mr-1 h-5 w-5" /> Personal Information
              </div>
              <button
                onClick={handleOutOfStation}
                className={`${
                  user?.status === 'Approved' ? 'bg-red-500' : 'bg-green-600'
                } text-white px-2 py-1 block ml-auto text-sm rounded-md transition-all hover:opacity-90`}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : user?.status === 'Approved' ? (
                  'Out Of Station'
                ) : (
                  'Resume'
                )}

                {/* <div class="toggle-button-cover">
                  <div id="button-3" class="button r">
                    <input class="checkbox" type="checkbox" />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div> */}
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p>
                <span className="font-medium">Name:</span> {user?.name}
              </p>
              <p>
                <span className="font-medium">Email:</span> {user?.emailId || 'Not Provided'}
              </p>
              <p>
                <span className="font-medium">Contact:</span> {user?.contactNo}
              </p>
              <p>
                <span className="font-medium">Alternate Contact:</span>{' '}
                {user?.alternateContact ? user?.alternateContact : 'Not Provided'}
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-6 px-2 text-green-700"
                  onClick={() => setShowAlternateContactModal(true)}
                >
                  Edit
                </Button>
              </p>
            </div>
          </CardContent>
        </Card>

        <OrderSettings
          minimumOrderValue={minimumOrderValue}
          setMinimumOrderValue={setMinimumOrderValue}
          deliveryCharges={deliveryCharges}
          setDeliveryCharges={setDeliveryCharges}
          addDeliveryCharges={addDeliveryCharges}
          setAddDeliveryCharges={setAddDeliveryCharges}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          handleUpdate={handleUpdate}
          loading={loading}
          user={user}
        />

        <Card className="bg-white shadow-sm">
          <CardHeader className="px-4 py-3 space-y-0 flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center">
              <ShoppingBag className="mr-2 h-5 w-5" /> Service Areas
            </CardTitle>
            <div className="text-base font-medium text-green-700">
              {user?.serviceArea?.length || 0} areas
            </div>
          </CardHeader>
          <CardContent className="px-4 py-3">
            <div className="px-2 pb-3">
              <ScrollArea className="h-fit max-h-44 overflow-y-auto rounded-md">
                {user?.serviceArea?.length > 0 ? (
                  user.serviceArea.map((item, idx) => (
                    <div
                      key={idx}
                      className="border-b border-dashed border-slate-300 last:border-none py-2"
                    >
                      <p className="text-sm">
                        {item?.societyName}, Plot/Pocket No {item?.plotNo}, Sector {item?.sector}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 py-2">No service areas added yet</p>
                )}
              </ScrollArea>
            </div>
            <Button
              onClick={() => navigate('/vendor/service/edit')}
              variant="outline"
              className="w-full mt-1"
            >
              Edit Service Areas
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm">
          <CardHeader className="px-4 py-3">
            <CardTitle className="text-lg font-semibold flex items-center">
              <MapPin className="mr-2 h-5 w-5" /> Address
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 py-3">
            {user?.address ? (
              <div className="space-y-1">
                <p>
                  {user?.address?.unitNo} {user?.address?.societyName},{' '}
                  {user?.address?.plotNo &&
                    `Plot No
                  ${user?.address?.plotNo}`}{' '}
                  {user?.address?.pocketNo &&
                    `Pocket No
                  ${user?.address?.pocketNo}`}
                </p>
                <p>
                  Sector {user?.address?.sector}, {user?.address?.locality}, {user?.address?.city}
                </p>
                <p>
                  {user?.address?.state}, {user?.address?.pinCode}, {user?.address?.country}
                </p>
              </div>
            ) : (
              <p className="text-gray-500">Address not provided</p>
            )}
          </CardContent>
        </Card>

        <AboutUsCard />

        <Button
          onClick={handleLogout}
          variant="destructive"
          className="w-fit block mx-auto mt-4"
          size="sm"
        >
          Logout
        </Button>
        {showAlternateContactModal && (
          <AlternateContactModal
            isOpen={showAlternateContactModal}
            onClose={() => setShowAlternateContactModal(false)}
            onUpdate={fetchData}
            alternateContact={alternateContact}
            setAlternateContact={setAlternateContact}
          />
        )}
      </main>

      <BottomNav />
    </div>
  );
};

const timeOptions = {};
for (let i = 0; i < 24; i++) {
  const hour = i.toString().padStart(2, '0');
  timeOptions[`${hour}:00`] = `${hour}:00`;
  timeOptions[`${hour}:30`] = `${hour}:30`;
}

const OrderSettings = ({
  minimumOrderValue,
  setMinimumOrderValue,
  deliveryCharges,
  setDeliveryCharges,
  addDeliveryCharges,
  setAddDeliveryCharges,
  selectedCategories,
  setSelectedCategories,
  handleUpdate,
  loading,
  user,
}) => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const scrollRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [updatedCategories, setUpdatedCategories] = useState([]);
  const [offHours, setOffHours] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');

  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const { data } = await axiosInstance.get(
          `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/products/categories`,
          {
            headers: {
              sessionToken: localStorage.getItem('sessionToken'),
              refreshToken: localStorage.getItem('refreshToken'),
            },
          }
        );

        const existingCategories = data.filter((item) => {
          return selectedCategories.includes(item.name);
        });

        const formatedCategories = existingCategories.map((item) => item.id);
        setUpdatedCategories(formatedCategories);
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
        toast.error('Failed to load categories');
      } finally {
        setLoadingCategories(false);
      }
    };
    if (user) {
      fetchCategories();
      const time = user?.offHours?.split('-') || [];
      if (time.length == 2) {
        setFromTime(time.at(0));
        setToTime(time.at(1));
      }
    }
  }, [user]);

  const toggleCategory = (categoryId) => {
    setUpdatedCategories((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  useEffect(() => {
    const handleScrollUpdate = () => {
      if (scrollRef.current) {
        setScrollPosition(scrollRef.current.scrollLeft);
      }
    };

    const currentRef = scrollRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScrollUpdate);
      return () => {
        currentRef.removeEventListener('scroll', handleScrollUpdate);
      };
    }
  }, []);

  useEffect(() => {
    if (fromTime && toTime) {
      setOffHours(`${fromTime}-${toTime}`);
    } else {
      setOffHours('');
    }
  }, [fromTime, toTime]);

  return (
    <Card className="bg-white shadow-sm">
      <CardHeader className="px-4 py-3">
        <CardTitle className="text-lg font-semibold flex items-center">
          <Settings className="mr-2 h-5 w-5" /> Order Settings & Categories
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-gray-900">Your Categories</h3>
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                {updatedCategories.length} selected
              </Badge>
            </div>

            <div
              ref={scrollRef}
              className="scrollbar-hide grid grid-cols-3  w-full pb-2 gap-3 py-2 scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {loadingCategories ? (
                Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <div key={i} className="flex flex-col items-center gap-2 flex-shrink-0">
                      <Skeleton className="w-16 h-16 rounded-full" />
                      <Skeleton className="w-12 h-3" />
                    </div>
                  ))
              ) : categories.length > 0 ? (
                categories.map((category) => (
                  <button
                    key={category?.id}
                    type="button"
                    onClick={() => toggleCategory(category.id)}
                    className={`px-3 min-w-20 col-span-1 py-2  rounded-lg flex flex-col justify-between items-center transition-all duration-300 ${
                      updatedCategories.includes(category.id)
                        ? 'bg-green-500 text-white'
                        : 'bg-slate-100 text-gray-800 hover:bg-gray-200'
                    }`}
                    aria-pressed={updatedCategories.includes(category.id)}
                  >
                    {/* <span className="text-2xl mr-2">{category.icon}</span> */}
                    <img src={`/${category?.name}.png`} alt={category?.name} className="w-8 h-8" />
                    {category.name}
                    {updatedCategories.includes(category.id) ? (
                      <CheckSquare className="w-5 h-5" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                  </button>
                ))
              ) : (
                <p className="text-sm text-gray-500 px-2 py-4">No categories available</p>
              )}
            </div>
          </div>

          <div className="space-y-4 pt-2">
            <div>
              <label htmlFor="minimum-order" className="block text-sm font-medium text-gray-700">
                Minimum Order Value (₹)
              </label>
              <Input
                id="minimum-order"
                type="number"
                value={minimumOrderValue}
                onChange={(e) => setMinimumOrderValue(e.target.value)}
                className="mt-1"
                placeholder="Enter minimum order value"
                required
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Add delivery charges to all products?
              </label>
              <div className="mt-2 flex justify-start space-x-3">
                <button
                  type="button"
                  onClick={() => setAddDeliveryCharges(false)}
                  className={`${
                    !addDeliveryCharges ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
                  } px-4 py-2 rounded-md transition-colors flex-1 text-sm font-medium`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setAddDeliveryCharges(true)}
                  className={`${
                    addDeliveryCharges ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
                  } px-4 py-2 rounded-md transition-colors flex-1 text-sm font-medium`}
                >
                  No
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="delivery-charges" className="block text-sm font-medium text-gray-700">
                Delivery Charges (₹)
              </label>
              <Input
                id="delivery-charges"
                type="number"
                value={deliveryCharges}
                onChange={(e) => setDeliveryCharges(e.target.value)}
                className="mt-1"
                placeholder="e.g, 0"
                required
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <p className="block text-sm font-medium text-gray-700">Shop Timings</p>
              <div className="flex flex-wrap gap-3 pt-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">From</label>
                  <select
                    className="w-24 border rounded-md px-2 py-1"
                    value={fromTime}
                    onChange={(e) => setFromTime(e.target.value)}
                  >
                    <option value="">Select</option>
                    {Object.entries(timeOptions).map(([key, value]) => (
                      <option key={key} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">To</label>
                  <select
                    className="w-24 border rounded-md px-2 py-1"
                    value={toTime}
                    onChange={(e) => setToTime(e.target.value)}
                  >
                    <option value="">Select</option>
                    {Object.entries(timeOptions).map(([key, value]) => (
                      <option key={key} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <Button
            onClick={() => handleUpdate(updatedCategories, offHours)}
            className="w-full bg-green-700 hover:bg-green-800 mt-2"
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? 'Updating...' : 'Save Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AboutUsCard = () => {
  return (
    <Card className="bg-white shadow-sm relative">
      <CardHeader className="pt-3 pb-3 px-4">
        <CardTitle className="text-lg font-semibold flex items-center">
          <div className="flex items-center justify-center gap-1">
            <a
              href="https://x.com/biglocal_in?s=11&t=kiWAF9DPzSDHAz67znvIbw"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <Twitter className="h-5 w-5 text-gray-700" />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61572946807977"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <Facebook className="h-5 w-5 text-gray-700" />
            </a>
            <a
              href="https://www.instagram.com/biglocal_in?igsh=MnpqZGNrcjNveGoy&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <Instagram className="h-5 w-5 text-gray-700" />
            </a>
            <a
              href="https://www.linkedin.com/company/biglocal-in/posts/?feedView=all"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <Linkedin className="h-5 w-5 text-gray-700" />
            </a>
            <a
              href="https://youtube.com/@biglocal-t8w?si=cnUvLXvxs766To6w"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <Youtube className="h-5 w-5 text-gray-700" />
            </a>
          </div>
          <Button
            variant="default"
            className="w-fit px-4 py-1.5 h-auto bg-green-700 hover:bg-green-800 text-white rounded-md text-sm ml-auto"
            onClick={() => window.open('https://about.biglocal.in/', '_blank')}
          >
            About Us
          </Button>
        </CardTitle>
      </CardHeader>
    </Card>
  );
};

const AlternateContactModal = ({
  isOpen,
  onClose,
  onUpdate,
  alternateContact,
  setAlternateContact,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { user, refreshUser } = useUser();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const apiData = {
        name: user?.name,
        shopName: user?.shopName,
        emailId: user?.emailId,
        contactNo: user?.contactNo,
        alternateContact: alternateContact,
        minimumOrder: user?.minimumOrder,
        deliveryCharge: user?.deliveryCharge,
        deliveryChargeWaived: user?.deliveryChargeWaived,
        categoryIds: user?.vendorCategories?.map((cat) => cat.id) || [],
        offHours: user?.offHours || '',
      };

      await axiosInstance.put(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/vendor`,
        apiData,
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
        }
      );

      toast.success('Alternate contact updated successfully');
      onUpdate();
      // refreshUser()
      onClose();
    } catch (error) {
      console.error('Error updating alternate contact:', error);
      toast.error('Failed to update alternate contact');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Update Alternate Contact</h2>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="p-4">
            <label
              htmlFor="alternateContact"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Alternate Contact Number
            </label>
            <Input
              id="alternateContact"
              type="tel"
              value={alternateContact}
              onChange={(e) => setAlternateContact(e.target.value)}
              placeholder="Enter alternate contact number"
              className="w-full"
              pattern="[0-9]{10}"
              maxLength={10}
            />
            <p className="text-xs text-gray-500 mt-1">Enter a 10-digit phone number</p>
          </div>
          <div className="p-4 border-t flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} className="bg-green-700 hover:bg-green-800">
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorProfile;
